import React from 'react';

import { useIntl } from 'react-intl';

import Badge from '@atlaskit/badge';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import PersonIcon from '@atlaskit/icon/core/person';

import type { SelectFilterOption } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys } from '../../../../../common/constants/product-keys';
import { useProductConfig } from '../../../../../common/constants/products';
import { formatAvatarOptionLabel } from '../../../../../common/ui/filters/select-filter/option-label';
import { FilterButton } from '../../../../../common/ui/quick-find/filter-button';
import { FilterButtonIconContainer } from '../../../../../common/ui/quick-find/filter-button/filter-button-icon-container';
import { FilterPopupSelect } from '../../../../../common/ui/quick-find/filter-popup-select';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useFiltersDiscoverability2Enabled } from '../../../../../controllers/store/bootstrap';
import { filter as contributorFilter } from '../../../../../controllers/store/search/filters/contributor';

import { i18n } from './messages';

export const ContributorFilter = ({
	iconBefore,
	iconAfter,
	onFilterClose,
}: {
	iconBefore?: React.ReactNode;
	iconAfter?: React.ReactNode;
	onFilterClose?: (options: SelectFilterOption[], selectedOptions: SelectFilterOption[]) => void;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [
		filterData,
		{
			loadInitialOptions,
			setLookupInput,
			setOption: setOptionForContributorFilter,
			clearLookupInput,
			clearOptions,
		},
	] = useSelectFilter({
		filterId: contributorFilter.id,
	});

	const confluenceProductConfig = useProductConfig(ProductKeys.Confluence);

	if (!filterData) {
		return null;
	}

	const {
		isLoading,
		suggestedOptions,
		selectedOptions,
		options,
		isLookupDefined,
		lookupInput,
		isLookupActive,
		badgeLabel,
		firstSelectedOptionLabel,
	} = filterData;

	const groupedOptions = [
		{
			label: formatMessage(i18n.confluenceFilterLabelSelected),
			options: selectedOptions,
		},
		{
			label: formatMessage(i18n.confluenceFilterLabelUnselected),
			options: suggestedOptions,
		},
	];

	const ConfluenceIcon = confluenceProductConfig.Icon;

	const getIconBefore = () => {
		if (iconBefore) {
			return iconBefore;
		}

		return (
			<FilterButtonIconContainer>
				<ConfluenceIcon size="xxsmall" />
				<PersonIcon label={formatMessage(i18n.contributor)} />
			</FilterButtonIconContainer>
		);
	};

	const getIconAfter = () => {
		return selectedOptions.length === 0 ? (
			iconAfter ? (
				iconAfter
			) : (
				<ChevronDownIcon label="" size="small" />
			)
		) : (
			badgeLabel && <Badge appearance="information">{badgeLabel}</Badge>
		);
	};

	const setOption = (option: SelectFilterOption | SelectFilterOption[]) => {
		setOptionForContributorFilter(option, undefined, ProductKeys.Confluence);
	};

	const onDropdownClose = () => {
		clearLookupInput();

		if (onFilterClose) {
			onFilterClose(options, selectedOptions);
		}
	};

	return (
		<FilterPopupSelect
			filterId={contributorFilter.id}
			canClearFilter
			checkboxIndicators
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={onDropdownClose}
			onSearchInputChange={setLookupInput}
			onSelectChange={setOption}
			options={isLookupActive ? options : groupedOptions}
			selectedOptions={selectedOptions}
			label={formatMessage(i18n.contributor)}
			placeholder={formatMessage(i18n.confluenceFilterContributorPlaceholder)}
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			formatOptionLabel={formatAvatarOptionLabel}
			onClearOptions={clearOptions}
			target={({ isOpen, ...targetProps }) => (
				<FilterButton
					testId="search-dialog-contributor-filter-button"
					{...targetProps}
					isSelected={!!selectedOptions.length || isOpen}
					iconBefore={getIconBefore()}
					iconAfter={getIconAfter()}
					minWidthZero={true}
				>
					{firstSelectedOptionLabel ? firstSelectedOptionLabel : formatMessage(i18n.contributor)}
				</FilterButton>
			)}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
		/>
	);
};
